import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { TfiLocationPin } from "react-icons/tfi";
import { FaCloud, FaDatabase, FaAws, FaMicrosoft } from "react-icons/fa";
import ApiService from "../../nhrc_apis/ApiService";

const { JobAPI } = ApiService;

/* ================= HELPER ================= */
const getJobStatusByDeadline = (deadline) => {
  if (!deadline) return "Open";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const jobDeadline = new Date(deadline);
  jobDeadline.setHours(0, 0, 0, 0);

  return jobDeadline < today ? "Closed" : "Open";
};

/* ================= JOB ICON (display-only) ================= */
const getJobIcon = (title = "") => {
  const t = title.toLowerCase();

  if (t.includes("azure")) {
    return (
      <div className="hrjob-icon hrjob-icon-azure">
        <FaMicrosoft />
      </div>
    );
  }

  if (t.includes("aws")) {
    return (
      <div className="hrjob-icon hrjob-icon-aws">
        <FaAws />
      </div>
    );
  }

  if (t.includes("db") || t.includes("database")) {
    return (
      <div className="hrjob-icon hrjob-icon-db">
        <FaDatabase />
      </div>
    );
  }

  return (
    <div className="hrjob-icon hrjob-icon-default">
      <FaCloud />
    </div>
  );
};

function HrDashboardHome() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ================= LOGGED IN USER ================= */
  const loggedUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};

  const loggedHrMemberId = String(
    loggedUser.membership_id ||
    loggedUser.memberId ||
    loggedUser.employee_id ||
    loggedUser.membershipId ||
    loggedUser.id ||
    ""
  ).trim();

  const getDisplayName = () => {
    const name =
      loggedUser.full_name ||
      loggedUser.employee_name ||
      loggedUser.name ||
      loggedUser.hrName;

    const isPlaceholder =
      !name || name.trim() === "" || name.trim().toLowerCase() === "string";

    if (!isPlaceholder) return name;

    return loggedUser?.email?.split("@")[0] || "HR";
  };

  /* ================= STATE ================= */
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [applicationCounts, setApplicationCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState({});
  const [lockedJobs, setLockedJobs] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({ status: "" });

  const pageSize = 4;

  /* ================= LOAD JOBS (API) ================= */
  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await JobAPI.getDashboard();
      let jobsData = [];

      if (Array.isArray(response?.data)) jobsData = response.data;
      else if (Array.isArray(response?.data?.jobs)) jobsData = response.data.jobs;
      else if (Array.isArray(response?.data?.data)) jobsData = response.data.data;
      else if (Array.isArray(response?.data?.posted_jobs)) jobsData = response.data.posted_jobs;
      else if (Array.isArray(response?.data?.dashboard_jobs)) jobsData = response.data.dashboard_jobs;

      const filteredJobs = jobsData.filter((job) => {
        const jobHrId = String(
          job.created_by ||
          job.createdBy ||
          job.hr_id ||
          job.hrId ||
          job.membership_id ||
          job.employee_id ||
          job.posted_by ||
          job.postedBy ||
          ""
        ).trim();
        return jobHrId === loggedHrMemberId;
      });

      setJobs(filteredJobs);
      fetchAllApplicationCounts(filteredJobs);
    } catch (error) {
      console.log("LOAD JOBS ERROR:", error?.response?.data || error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedHrMemberId]);

  /* ================= FETCH APPLICATION COUNTS (API) ================= */
  const fetchAllApplicationCounts = useCallback(async (jobsList) => {
    if (!jobsList || jobsList.length === 0) return;

    const approvedJobs = jobsList.filter(
      (j) => (j.status || "").toUpperCase() === "APPROVED"
    );
    const nonApprovedJobs = jobsList.filter(
      (j) => (j.status || "").toUpperCase() !== "APPROVED"
    );

    if (nonApprovedJobs.length > 0) {
      const initialLocked = {};
      nonApprovedJobs.forEach((j) => { initialLocked[j.id] = true; });
      setLockedJobs((prev) => ({ ...prev, ...initialLocked }));
    }

    if (approvedJobs.length === 0) return;

    const loadingState = {};
    approvedJobs.forEach((j) => { loadingState[j.id] = true; });
    setCountsLoading((prev) => ({ ...prev, ...loadingState }));

    const results = await Promise.allSettled(
      approvedJobs.map((job) =>
        JobAPI.getHrJobApplications(job.id)
          .then((res) => {
            const data = res?.data;
            let count = 0;
            let shortlisted = 0;
            let rejected = 0;
            let list = [];

            if (Array.isArray(data)) list = data;
            else if (Array.isArray(data?.applications)) list = data.applications;
            else if (Array.isArray(data?.data)) list = data.data;
            else if (Array.isArray(data?.results)) list = data.results;
            else if (Array.isArray(data?.applicants)) list = data.applicants;

            if (list.length) {
              count = list.length;
              shortlisted = list.filter(
                (c) => (c.status || "").toLowerCase() === "shortlisted"
              ).length;
              rejected = list.filter(
                (c) => (c.status || "").toLowerCase() === "rejected"
              ).length;
            } else if (typeof data?.total === "number") {
              count = data.total;
            } else if (typeof data?.count === "number") {
              count = data.count;
            }

            return { jobId: job.id, count, shortlisted, rejected, locked: false };
          })
          .catch((err) => ({
            jobId: job.id,
            count: 0,
            shortlisted: 0,
            rejected: 0,
            locked: err?.response?.status === 403,
          }))
      )
    );

    const countsMap = {};
    const lockedMap = {};
    const clearLoad = {};
    results.forEach((r) => {
      if (r.status === "fulfilled") {
        countsMap[r.value.jobId] = {
          total: r.value.count,
          shortlisted: r.value.shortlisted,
          rejected: r.value.rejected,
        };
        lockedMap[r.value.jobId] = r.value.locked;
        clearLoad[r.value.jobId] = false;
      }
    });

    setApplicationCounts((prev) => ({ ...prev, ...countsMap }));
    setLockedJobs((prev) => ({ ...prev, ...lockedMap }));
    setCountsLoading((prev) => {
      const updated = { ...prev };
      Object.keys(clearLoad).forEach((id) => { delete updated[id]; });
      return updated;
    });
  }, []);

  useEffect(() => {
    loadJobs();
  }, [location.key, loadJobs]);

  /* ================= FILTER JOBS ================= */
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const search = searchInput.toLowerCase();
      const matchSearch =
        (job.title || "").toLowerCase().includes(search) ||
        (job.location || "").toLowerCase().includes(search);
      const matchStatus =
        !filters.status ||
        (job.status || "").toLowerCase() === filters.status.toLowerCase();
      return matchSearch && matchStatus;
    });
  }, [jobs, searchInput, filters.status]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredJobs.length / pageSize);

  const currentJobs = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredJobs.slice(startIndex, startIndex + pageSize);
  }, [filteredJobs, page]);

  /* ================= SELECT ================= */
  const allSelected =
    currentJobs.length > 0 &&
    currentJobs.every((job) => selectedIds.includes(job.id));

  const handleToggleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? currentJobs.map((j) => j.id) : []);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ================= GET COUNTS FOR A JOB (from API state) ================= */
  const getCounts = (job) => {
    const c = applicationCounts[job.id];
    return {
      total: c?.total ?? 0,
      shortlisted: c?.shortlisted ?? job.shortlisted ?? 0,
      rejected: c?.rejected ?? job.rejected ?? 0,
    };
  };

  /* ================= OVERVIEW STATS (derived from API data) ================= */
  const overviewStats = useMemo(() => {
    const totalJobs = jobs.length;

    const shortlisted = Object.values(applicationCounts).reduce(
      (sum, c) => sum + (c.shortlisted || 0),
      0
    );

    const pendingApproval = jobs.filter(
      (j) => (j.status || "").toUpperCase() === "PENDING"
    ).length;

    const rejected = jobs.filter(
      (j) => (j.status || "").toUpperCase() === "REJECTED"
    ).length;

    return { totalJobs, shortlisted, pendingApproval, rejected };
  }, [jobs, applicationCounts]);

  /* ================= ACTIONS ================= */
  const handleSearch = () => setPage(1);

  const handleDelete = async () => {
    if (!selectedIds.length) { alert("Please select job(s)"); return; }
    if (!window.confirm(`Delete ${selectedIds.length} selected job(s)?`)) return;

    try {
      for (const id of selectedIds) await JobAPI.deleteJob(id);
      alert("Jobs Deleted Successfully");
      setSelectedIds([]);
      loadJobs();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete Failed");
    }
  };

  const handleAddJob = () => navigate("/hrdashboard/Livejobpost");

  const handlePreview = (job) => {
    navigate("/hrdashboard/Livejobpost", {
      state: {
        editJob: {
          id: job.id,
          title: job.title || "",
          company_name: job.company_name || "",
          department: job.department || "",
          work_mode: job.work_mode || "",
          roles_responsibilities: job.roles_responsibilities || "",
          qualification_required: job.qualification_required || "",
          required_skills: job.required_skills || "",
          min_experience: job.min_experience || "",
          max_experience: job.max_experience || "",
          location: job.location || "",
          locality: job.locality || "",
          openings: job.openings || "",
          min_salary: job.min_salary || "",
          max_salary: job.max_salary || "",
          whatsapp_number: job.whatsapp_number || "",
          application_deadline: job.application_deadline || "",
          logo: job.logo || "",
          perks_benefits: job.perks_benefits || "",
          skills: job.required_skills ? job.required_skills.split(",") : [],
          perks: job.perks_benefits ? job.perks_benefits.split(",") : [],
        },
      },
    });
  };

  const handleViewResponses = (job) => {
    if (lockedJobs[job.id]) {
      alert("Applications will be available after admin approves this job.");
      return;
    }
    navigate(`/hrdashboard/TotalResponses/${job.id}`, {
      state: { jobTitle: job.title, jobId: job.id },
    });
  };

  /* ================= UI ================= */
  return (
    <>
      <style>{`
        .hrdash-wrapper {
          font-family: "Poppins", sans-serif;
          background: #f7f8fa;
          min-height: 100vh;
        }

        .hrdash-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }

        .hrdash-welcome-title {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 4px 0;
        }

        .hrdash-sub-info {
          display: flex;
          gap: 20px;
          font-size: 13px;
          color: #666;
          flex-wrap: wrap;
        }

        .hrdash-sub-info b {
          color: #222;
        }

        .hrdash-postjob-btn {
          background: #198754;
          border: none;
          color: #fff;
          font-weight: 600;
          padding: 9px 18px;
          border-radius: 6px;
          font-size: 14px;
        }

        .hrdash-postjob-btn:hover {
          background: #157347;
        }

        .hrdash-actionbar {
          background: #ffffff;
          border: 1px solid #e9e9e9;
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: 20px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 14px;
        }

        .hrjob-card {
          background: #ffffff;
          border: 1px solid #ececec;
          border-radius: 10px;
          padding: 18px;
          margin-top: 16px;
        }

        .hrjob-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: #fff;
          flex-shrink: 0;
        }

        .hrjob-icon-azure { background: #0078d4; }
        .hrjob-icon-aws { background: #ff9900; }
        .hrjob-icon-db { background: #6f42c1; }
        .hrjob-icon-default { background: #6c757d; }

        .hrjob-title {
          font-size: 15px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .hrjob-location {
          font-size: 12px;
          color: #888;
        }

        .hrjob-pill {
          font-size: 12px;
          font-weight: 600;
          border-radius: 6px;
          padding: 6px 14px;
          border: 1px solid;
          background: #fff;
        }

        .hrjob-pill-total {
          color: #0d6efd;
          border-color: #0d6efd;
        }

        .hrjob-pill-shortlisted {
          color: #198754;
          border-color: #198754;
        }

        .hrjob-pill-rejected {
          color: #dc3545;
          border-color: #dc3545;
        }

        .hrjob-admin-label {
          font-size: 13px;
          color: #555;
        }

        .hrjob-status-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
        }

        .hrjob-status-open {
          background: #d1f5dd;
          color: #157347;
        }

        .hrjob-status-closed {
          background: #fbdada;
          color: #dc3545;
        }

        .hrjob-posted-date {
          font-size: 11px;
          color: #d99a00;
          font-weight: 600;
        }

        .hrdash-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-top: 24px;
          margin-bottom: 14px;
        }

        .hrdash-stats-grid-secondary {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 14px;
          margin-bottom: 24px;
        }

        .hrdash-stat-card {
          background: #ffffff;
          border: 1px solid #ececec;
          border-radius: 10px;
          padding: 14px;
          text-align: center;
        }

        .hrdash-stat-value {
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .hrdash-stat-label {
          font-size: 12px;
          color: #888;
          margin: 2px 0 0 0;
        }

        .hrdash-announcement {
          background: #eaf3ff;
          border: 1px solid #cfe4ff;
          border-radius: 8px;
          padding: 10px 16px;
          font-size: 13px;
          color: #333;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .hrdash-announcement a {
          font-weight: 600;
          color: #0d6efd;
          text-decoration: none;
        }

        @media (max-width: 992px) {
          .hrdash-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .hrdash-stats-grid-secondary {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 576px) {
          .hrdash-stats-grid,
          .hrdash-stats-grid-secondary {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      <section className="hrdash-wrapper">
        <div className="container py-4">

          {/* ================= WELCOME HEADER ================= */}
          <div className="hrdash-header">
            <div>
              <h4 className="hrdash-welcome-title">
                Welcome {getDisplayName()} 
              </h4>
              <div className="hrdash-sub-info">
                <span>
                  Member ID: <b>{loggedHrMemberId || "—"}</b>
                </span>
                <span>
                  Total Jobs: <b>{jobs.length}</b>
                </span>
              </div>
            </div>

            <button className="hrdash-postjob-btn" onClick={handleAddJob}>
              + Post Job
            </button>
          </div>

          {/* ================= ACTION BAR ================= */}
          <div className="hrdash-actionbar">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={allSelected}
                onChange={handleToggleSelectAll}
              />
              <label className="form-check-label ms-1" style={{ color: "#333" }}>
                Select All
              </label>
            </div>

            <div className="flex-grow-1 d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Job Title or Location..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
              />
              <button className="btn btn-primary" onClick={handleSearch}>
                Search
              </button>
            </div>

            <div style={{ width: "150px" }}>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <button className="btn btn-outline-danger" onClick={handleDelete}>
              Delete ({selectedIds.length})
            </button>
          </div>

          {/* ================= LOADING ================= */}
          {loading && (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <div className="spinner-border text-primary" role="status" />
              <p style={{ marginTop: "12px", color: "#888" }}>Loading Jobs...</p>
            </div>
          )}

          {/* ================= JOB LIST ================= */}
          {!loading && currentJobs.map((job) => {
            const counts = getCounts(job);
            const computedStatus = getJobStatusByDeadline(job.application_deadline);
            const isCountLoading = !!countsLoading[job.id];
            const isLocked = !!lockedJobs[job.id];

            return (
              <div key={job.id} className="hrjob-card">

                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex gap-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(job.id)}
                      onChange={() => handleToggleSelect(job.id)}
                      style={{ marginTop: "6px" }}
                    />

                    {getJobIcon(job.title)}

                    <div>
                      <h6 className="hrjob-title">{job.title}</h6>
                      <small className="hrjob-location">
                        <TfiLocationPin /> {job.location}
                      </small>
                    </div>
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handlePreview(job)}
                  >
                    Job Preview
                  </button>
                </div>

                <div className="d-flex justify-content-center gap-3 my-3">
                  <button
                    className="hrjob-pill hrjob-pill-total"
                    disabled={isCountLoading}
                    onClick={() => handleViewResponses(job)}
                  >
                    {isCountLoading
                      ? "Loading..."
                      : isLocked
                      ? "Pending Approval"
                      : `${counts.total} Responses`}
                  </button>

                  <button className="hrjob-pill hrjob-pill-shortlisted">
                    {counts.shortlisted} Shortlisted
                  </button>

                  <button className="hrjob-pill hrjob-pill-rejected">
                    {counts.rejected} Rejected
                  </button>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <p className="hrjob-admin-label mb-0">
                    Admin:
                    <span
                      className="ms-2 fw-bold"
                      style={{
                        color:
                          job.status === "APPROVED"
                            ? "green"
                            : job.status === "REJECTED"
                            ? "red"
                            : "orange",
                      }}
                    >
                      {job.status || "PENDING"}
                    </span>
                  </p>

                  <div className="text-end">
                    <span
                      className={`hrjob-status-badge ${
                        computedStatus === "Open"
                          ? "hrjob-status-open"
                          : "hrjob-status-closed"
                      }`}
                    >
                      {computedStatus}
                    </span>

                    <div className="hrjob-posted-date mt-1">
                      Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : "N/A"}
                    </div>
                  </div>
                </div>

              </div>
            );
          })}

          {/* ================= EMPTY ================= */}
          {!loading && currentJobs.length === 0 && (
            <div className="text-center py-5">
              <h6 className="text-muted">No jobs found</h6>
              <p className="text-muted small">
                Try adjusting your search filters
              </p>
              <button className="btn btn-success mt-3" onClick={handleAddJob}>
                <i className="bi bi-plus-circle me-2"></i>
                Post Your First Job
              </button>
            </div>
          )}

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between mt-4">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span style={{ fontSize: "13px", color: "#555", alignSelf: "center" }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}

          {/* ================= STATS GRID (ROW 1) ================= */}
          <div className="hrdash-stats-grid">
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{overviewStats.totalJobs}</p>
              <p className="hrdash-stat-label">Total Jobs (Live & Closed)</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{overviewStats.shortlisted}</p>
              <p className="hrdash-stat-label">Shortlisted Candidates</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{overviewStats.pendingApproval}</p>
              <p className="hrdash-stat-label">Pending Approval Jobs</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{overviewStats.rejected}</p>
              <p className="hrdash-stat-label">Rejected Jobs</p>
            </div>
          </div>

          {/* ================= STATS GRID (ROW 2 — placeholders until backend provides these) ================= */}
          <div className="hrdash-stats-grid-secondary">
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.profile_completion ?? "—"}</p>
              <p className="hrdash-stat-label">Profile Completion</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.applied_jobs ?? "—"}</p>
              <p className="hrdash-stat-label">Applied Jobs</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.interview_schedule ?? "—"}</p>
              <p className="hrdash-stat-label">Interview Schedule</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.training_certificates ?? "—"}</p>
              <p className="hrdash-stat-label">Training Certificates</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.event_registrations ?? "—"}</p>
              <p className="hrdash-stat-label">Event Registrations</p>
            </div>
            <div className="hrdash-stat-card">
              <p className="hrdash-stat-value">{loggedUser?.membership_validity ?? "—"}</p>
              <p className="hrdash-stat-label">Membership Validity</p>
            </div>
          </div>

          {/* ================= ANNOUNCEMENT BAR ================= */}
          <div className="hrdash-announcement">
            <b>Announcements:</b> New Job Fair in Hyderabad on 10th June 2026.{" "}
            <a href="#">Register Now!</a>
          </div>

        </div>
      </section>
    </>
  );
}

export default HrDashboardHome;